"use client";

import { useRef, useEffect, useCallback, useState } from "react";
import { Box, VStack, Text, Textarea, Button } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { DndContext } from "@dnd-kit/core";
import { useRag, useUserChoices } from "@/state/globalStore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import type { ChatBotMessage } from "../types";
import useChatBot from "../hooks/useChatBot";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import { useOriginActions } from "@/features/origins";
import { useSystemDesign } from "@/shared/system-design";
import ChatbotMessageDetails from "./ChatbotMessageDetails";
import ChatBotWaitingAnimation from "./ChatBotWaitingAnimation";
import ChatbotMessageReactions from "./ChatbotMessageReactions";
import WithControlButtons from "@/shared/ui/actions/WithControlButtons";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import BottomSheetDrawer from "./BottomSheetDrawer";
import ChatBotSettings from "./ChatBotSettings";
import {
  MINIMAL_ORIGIN_LENGTH_FOR_EXTRACTION,
  useSourceExtraction,
} from "@/features/backlog";
import {
  ExtractThoughtsRequest,
  SupportedLanguage,
  Target,
} from "@/generated/fastapi";
// const WithControlButtons = lazy(() => import("../Generic/WithControlButtons")); // Lazy load to reduce initial bundle size
// const WithSwipeSelector = lazy(() => import("../Generic/WithSwipeSelector")); // Lazy load to reduce initial bundle size

// --------------------------------
// Main Component
// --------------------------------

interface ChatBotProps {
  allowMessageReactions?: boolean;
  allowMessageDetails?: boolean;
}

export default function ChatBot({
  allowMessageReactions = false,
  allowMessageDetails = true,
}: ChatBotProps) {
  const { isMobileDevice } = useSystemDesign();
  const {
    chatMessages,
    deleteChatMessage,
    addChatMessage,
    addChatReaction,
    selectedChatMessages,
    removeSelectedChatMessage,
    toggleSelectedChatMessage,
    countSelectedChatMessages,
    waitingForBot,
  } = useRag();
  const {
    showScrollButton,
    setShowScrollButton,
    inputQuery,
    setInputQuery,
    handleSendMessage,
    sanitizeSchema,
  } = useChatBot();
  const { handleAddNewOriginClick } = useOriginActions();
  const {
    waitingForExtraction,
    handleSourceExtractionClick,
    extractingMessageId,
  } = useSourceExtraction();
  const { interimState } = useUserChoices();

  // Refs for auto-scrolling and input focus
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const nextLocalMessageId = useRef(-1);

  const chatWidth = "full";
  const chatHeight = "full"; // always take full height
  const messageProportionalWidth = "11/12"; // messages take up to about 90% of chat width to save "height" space

  // Component settings
  const userColor = "moveon.primaryLight";
  const botColor = "moveon.secondaryLight";
  const systemColor = "moveon.warningLight";

  // Clicking the selector area toggles visibility of the checkbox for that message.
  const [clickedSelectorId, setClickedSelectorId] = useState<number | null>(
    null,
  );
  // Bottom sheet drawer state
  const [drawerState, setDrawerState] = useState<"closed" | "half" | "full">(
    "closed",
  );
  const [referedTargets, setReferedTargets] = useState<Target[]>([]); // keep list to be compatible with GenericSelector

  // OR when clicking on a different message (unless items are already selected)
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const target = e.target as HTMLElement;

      // Check if click is on a checkbox or inside WithSwipeSelector component
      const isCheckboxClick = target.closest?.(".swipe-checkbox-root");
      const isWithinSwipeSelector = target.closest?.("[data-swipe-selector]");

      // Don't clear if clicking on checkbox or within the swipe selector that has the checkbox
      if (
        isCheckboxClick ||
        (isWithinSwipeSelector && clickedSelectorId !== null)
      ) {
        return;
      }

      // If clicked outside the messages container, always clear
      if (!container.contains(target)) {
        setClickedSelectorId(null);
        return;
      }

      // If some items are already selected, keep checkboxes visible
      if (selectedChatMessages && selectedChatMessages.length > 0) {
        return;
      }

      // If clicked inside container but on empty space or different message, clear
      setClickedSelectorId(null);
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [selectedChatMessages, clickedSelectorId]);

  // Memoized input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputQuery(e.target.value);
    },
    [setInputQuery],
  );

  // Handle keyboard shortcuts for sending messages
  const sendMessageKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.ctrlKey) {
        handleSendMessage({
          text: inputQuery,
          target_id:
            referedTargets.length > 0 ? referedTargets[0].id : undefined,
        });
      }
    },
    [inputQuery, handleSendMessage, referedTargets],
  );

  // Show welcome message on mount or when chat is empty
  useEffect(() => {
    if (chatMessages.length === 0) {
      const welcomeMsg: ChatBotMessage = {
        id: Date.now(),
        sender: "system",
        text: "Welcome! Type `/help` for available commands or start texting.",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      addChatMessage(welcomeMsg);
    }
  }, [chatMessages.length, addChatMessage]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, waitingForBot]);

  // Track scroll position to toggle scroll-to-bottom button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom =
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + 20;
      setShowScrollButton(!atBottom);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [setShowScrollButton]);

  // Load initial input from interim state if available
  useEffect(() => {
    const loadValue = interimState["open_chat"]?.value;
    if (loadValue && inputRef.current) {
      // Load string template
      const template = `in reference to: {${loadValue}} ... `;

      // setInputQuery(loadValue);
      setInputQuery(template);
    }
  }, [interimState, setInputQuery]);

  // Handle source extraction from message
  async function handleSourceExtractionFromMessage(msg: ChatBotMessage) {
    if (waitingForExtraction) {
      return;
    }

    if (msg.text.length < MINIMAL_ORIGIN_LENGTH_FOR_EXTRACTION) {
      return;
    }
    const origin = await handleAddNewOriginClick({
      raw_text_input: msg.text,
      sourced_where: "chatbot",
    });

    if (!origin) {
      return;
    }

    if (!origin.language) {
      return;
    }

    const extractRequest: ExtractThoughtsRequest = {
      origin_id: origin.id,
      selected_language: origin.language as SupportedLanguage,
      selected_prototype: null, // TODO Let backend decide
      sourced_where: "chatbot",
      origin_purpose: "mindstring_extraction",
      flag_as_processed: true,
    };

    const result = await handleSourceExtractionClick({
      request: extractRequest,
      messageId: msg.id,
    });

    // Add decision reasoning as system message if available
    if (result && result.decision_reasoning) {
      const reasoningMsg: ChatBotMessage = {
        id: nextLocalMessageId.current--,
        sender: "system",
        text:
          `Extraction reasoning: ${result.decision_reasoning}` +
          ` Review the extracted thoughts in your Backlog!`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      addChatMessage(reasoningMsg);
    }
  }

  return (
    <DndContext>
      <Box
        maxW={chatWidth}
        height={chatHeight}
        display="flex"
        flexDirection="column"
        overflowX="hidden" // controls horizontal overflow globally
      >
        {/* Messages + Drawer Area (grows to fill available space) */}
        <Box
          flex="1"
          position="relative" // enables absolute positioning for drawer
          overflowY="hidden" // prevent double scrollbars
        >
          {/* Messages */}
          <VStack
            spaceY="1" // vertical spacing between messages respecting timestamp
            align="stretch"
            height="full"
            overflowY="auto"
            overflowX="hidden"
            ref={messagesContainerRef}
            width="full"
            pb="12" // Add bottom padding for drawer handle space (48px = 12 * 4px)
          >
            <AnimatePresence>
              {chatMessages.map((msg) => {
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    style={{
                      display: "flex",
                      justifyContent:
                        msg.sender === "user"
                          ? "flex-end"
                          : msg.sender === "system"
                            ? "center"
                            : "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box
                      minWidth="64" // adjust to keep timestamp + actions buttons nicely visible
                      width={msg.sender === "system" ? "full" : "full"} //* System messages take full width, others have max width
                      maxW={
                        msg.sender === "system"
                          ? "full"
                          : messageProportionalWidth
                      } //* System messages take full width, others have max width
                      borderRadius="md"
                      p="2"
                      bg={
                        msg.sender === "user"
                          ? userColor
                          : msg.sender === "system"
                            ? systemColor
                            : botColor
                      }
                      position="relative"
                      wordBreak="break-word"
                      _hover={{
                        // @ts-ignore This works but Chakra/TS complains
                        "& .timestamp": { opacity: 0.5 },
                        // @ts-ignore This works but Chakra/TS complains
                        "& .actions": { opacity: 1, pointerEvents: "auto" },
                      }}
                    >
                      {/* Show mobile swipe UI on real mobile devices; on desktop use checkboxes based on component logic */}
                      <WithSwipeSelector
                        isMobile={isMobileDevice}
                        selection={{
                          isSelected:
                            selectedChatMessages?.includes(msg.id) ?? false,
                          onToggle: () => toggleSelectedChatMessage(msg.id),
                          showCheckboxOnDesktop:
                            selectedChatMessages.length > 0,
                          humanReadable: "Select message for batch operations",
                        }}
                        action={{
                          onSwipe: async () => {
                            await handleAddNewOriginClick({
                              raw_text_input: msg.text,
                              sourced_where: "chatbot",
                            });
                          },
                          icon: "add",
                          humanReadable: "Save as origin",
                          actionDescription: "Save as origin",
                        }}
                      >
                        <WithControlButtons
                          delete={{
                            onClick: () => {
                              deleteChatMessage(msg.id);
                              removeSelectedChatMessage(msg.id);
                            },
                            mode: "close",
                            humanReadable: "Delete chat message",
                          }}
                          primary={{
                            onClick: async () => {
                              await handleAddNewOriginClick({
                                raw_text_input: msg.text,
                                sourced_where: "chatbot",
                              });
                            },
                            icon: "add",
                            humanReadable: "Add message as source material",
                          }}
                          secondary={{
                            onClick: () =>
                              handleSourceExtractionFromMessage(msg),
                            icon: "extract",
                            humanReadable: "Shape thoughts from message",
                          }}
                          tertiary={{
                            onClick: () => toggleSelectedChatMessage(msg.id),
                            icon:
                              countSelectedChatMessages() === 0 &&
                              !isMobileDevice
                                ? "select"
                                : "unselect",
                            humanReadable:
                              countSelectedChatMessages() === 0 &&
                              !isMobileDevice
                                ? "Select message"
                                : "Unselect message",
                          }}
                          clipboard={{
                            value: msg.text,
                            humanReadable: "Copy message to clipboard",
                          }}
                          // childrenBorderBase={false}
                          showChildrenBorderBaseOnFocus={false}
                          loading={{
                            show: extractingMessageId === msg.id,
                            message: "Shaping thoughts...",
                          }}
                        >
                          <>
                            {/* Sender label */}
                            <Text fontSize="sm" fontWeight="bold" mb="1">
                              {msg.sender === "user"
                                ? "You"
                                : msg.sender === "bot"
                                  ? "MoveOn Profiler" // TODO consider moving this mapping elsewhere
                                  : "System"}
                            </Text>

                            {/* System reply  */}
                            {msg.sender === "bot" ? (
                              <Box
                                className="markdown-content"
                                overflowWrap="break-word"
                              >
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[
                                    [rehypeSanitize, sanitizeSchema],
                                  ]} // with custom schema
                                >
                                  {msg.text}
                                </ReactMarkdown>
                              </Box>
                            ) : (
                              <Text
                                // color="fg.primary" // not needed
                                overflowWrap="break-word"
                              >
                                {msg.text}
                              </Text>
                            )}
                          </>
                        </WithControlButtons>
                      </WithSwipeSelector>

                      {/* Message details should live inside the positioned Box so absolute positioning works */}
                      {allowMessageDetails && (
                        <ChatbotMessageDetails msg={msg} />
                      )}
                    </Box>

                    {/* Reactions */}
                    {msg.sender !== "system" && allowMessageReactions && (
                      <ChatbotMessageReactions
                        msg={msg}
                        addReaction={addChatReaction}
                        handleSendMessage={handleSendMessage}
                      />
                    )}

                    {/* Retry button */}
                    {msg.sender === "user" && msg.status === "error" && (
                      <Button
                        size="xs"
                        mt="1"
                        bg="moveon.primary"
                        // onClick={() => handleSendMessage(msg.text, msg.id)}
                        onClick={() => {
                          handleSendMessage({
                            text: msg.text,
                            retryId: msg.id,
                          });
                        }}
                      >
                        Retry
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Waiting for bot with animation */}
            {waitingForBot && <ChatBotWaitingAnimation />}

            <div ref={messagesEndRef} />
          </VStack>

          {/* Scroll-to-bottom button */}
          {showScrollButton && (
            <Button
              bg="moveon.primary"
              size="sm"
              position="absolute"
              bottom="40" // ensures it is above text input+send button area
              right="10"
              onClick={() =>
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <GenericIcon icon="scroll_down" />
            </Button>
          )}

          {/* Bottom Sheet Drawer for Settings */}
          <BottomSheetDrawer
            initialState="closed"
            showOverlay={true}
            onStateChange={setDrawerState}
          >
            <ChatBotSettings
              settingsState={drawerState}
              setInputQuery={setInputQuery}
              referedTargets={referedTargets}
              setReferedTargets={setReferedTargets}
            />
          </BottomSheetDrawer>
        </Box>

        {/* Texting area - Always visible at bottom with higher z-index */}
        <Box mt="3" position="relative" width="full" zIndex="max">
          <Textarea
            ref={inputRef}
            value={inputQuery}
            onChange={handleInputChange}
            placeholder="Type a message... (Ctrl+Enter to send) or use /commands (i.e. /help or /reset)"
            disabled={waitingForBot}
            onKeyDown={sendMessageKeyDown}
            autoresize
            paddingRight="14"
            borderRadius="md"
            minHeight="10"
          />
          <Button
            bg={inputQuery.trim() ? "moveon.primary" : "moveon.inactive"}
            size="sm"
            onClick={() => {
              handleSendMessage({
                text: inputQuery,
                target_id:
                  referedTargets.length > 0 ? referedTargets[0].id : undefined,
              });
            }}
            disabled={waitingForBot || !inputQuery.trim()}
            position="absolute"
            borderRadius="full" // make it round
            right="1.5"
            bottom="1.5"
            height="8"
            width="8"
            _hover={{
              transform: inputQuery.trim() ? "scale(1.1)" : "none",
              bg: inputQuery.trim() ? "moveon.primary" : "moveon.inactive",
            }}
            _active={{
              transform: "scale(0.95)",
            }}
            // @ts-ignore This works but Chakra/TS complains
            transition="all 0.2s"
          >
            <GenericIcon icon="send" size={iconSizePresets.toolbar} />
          </Button>
        </Box>
      </Box>
    </DndContext>
  );
}
